import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check, Sparkles, Users } from "lucide-react";
import api from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { toast } from "sonner";

const FEATURES_FREE = [
  "1 projet actif",
  "30 tâches par projet",
  "Vue Gantt / Liste / Kanban",
  "Un seul utilisateur",
];

const FEATURES_PRO = [
  "Projets et tâches illimités",
  "Export PDF, PNG et XLSX",
  "Retrait du filigrane FlowGantt",
  "Générateur IA de plannings",
  "Templates & dossiers illimités",
  "Support prioritaire",
];

const FEATURES_TEAM = [
  "Tout ce qui est dans Pro",
  "5 sièges inclus (6€ par siège en plus)",
  "Rôles Manager / Membre",
  "Partage de projets et absences d'équipe",
  "Logo personnalisé sur les exports",
];

export default function UpgradeModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null); // 'pro' | 'team' | null
  const [plan, setPlan] = useState("monthly");
  const navigate = useNavigate();

  const inTeam = !!user?.team_id;
  const isPro = !!user?.is_pro && !inTeam;

  const handleUpgradePro = async () => {
    setLoading("pro");
    try {
      const lookupKey =
        plan === "yearly" ? "flowgantt_pro_yearly" : "flowgantt_pro_monthly";
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(null);
    }
  };

  const handleUpgradeTeam = async () => {
    setLoading("team");
    try {
      const { data } = await api.post("/team/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="upgrade-modal"
        className="rounded-sm max-w-5xl p-0 overflow-hidden"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Choisir un plan</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3">
          {/* Free */}
          <div className="p-8 border-r border-stone-200 bg-stone-50" data-testid="plan-card-free">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-semibold mb-4">
              {!isPro && !inTeam ? "Plan actuel" : "Gratuit"}
            </div>
            <div className="font-display font-black text-3xl tracking-tighter">
              Free
            </div>
            <div className="text-neutral-500 text-sm mt-1">Pour démarrer</div>
            <div className="font-display font-black text-4xl mt-6 tracking-tighter">
              0€
            </div>
            <div className="text-xs text-neutral-500 mt-1">/ mois</div>
            <ul className="space-y-3 mt-8">
              {FEATURES_FREE.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-neutral-400 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div className="p-8 bg-neutral-950 text-white relative border-r border-neutral-800" data-testid="plan-card-pro">
            <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.25em] bg-[#E05236] text-white px-2 py-1 rounded-sm font-semibold">
              Recommandé
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-4">
              <Sparkles className="w-3 h-3 inline mr-1" />
              {isPro ? "Ton plan" : "Passer à Pro"}
            </div>
            <div className="font-display font-black text-3xl tracking-tighter text-white">
              Pro
            </div>
            <div className="text-neutral-400 text-sm mt-1">Pour les indépendants</div>

            <div className="mt-6 inline-flex rounded-sm border border-neutral-700 p-0.5">
              <button
                data-testid="plan-monthly-btn"
                onClick={() => setPlan("monthly")}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-semibold rounded-sm transition-colors ${
                  plan === "monthly" ? "bg-white text-neutral-950" : "text-neutral-400"
                }`}
              >
                Mensuel
              </button>
              <button
                data-testid="plan-yearly-btn"
                onClick={() => setPlan("yearly")}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-semibold rounded-sm transition-colors ${
                  plan === "yearly" ? "bg-white text-neutral-950" : "text-neutral-400"
                }`}
              >
                Annuel <span className="text-[#E05236]">-17%</span>
              </button>
            </div>

            <div className="mt-6 flex items-baseline gap-2">
              <div className="font-display font-black text-4xl tracking-tighter">
                {plan === "yearly" ? "90€" : "9€"}
              </div>
              <div className="text-neutral-400 text-sm">
                / {plan === "yearly" ? "an" : "mois"}
              </div>
            </div>

            <ul className="space-y-3 mt-8">
              {FEATURES_PRO.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#E05236] shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>

            <Button
              data-testid="checkout-pro-btn"
              onClick={handleUpgradePro}
              disabled={loading !== null || isPro || inTeam}
              className="w-full mt-8 rounded-sm bg-[#E05236] hover:bg-[#C94A31] text-white h-12 disabled:opacity-50"
            >
              {isPro
                ? "Tu es déjà Pro"
                : inTeam
                ? "Déjà en équipe"
                : loading === "pro"
                ? "Redirection…"
                : "Passer à Pro"}
            </Button>
            <p className="text-[10px] text-neutral-500 mt-3 text-center">
              Paiement sécurisé Stripe · Sans engagement
            </p>
          </div>

          {/* Team */}
          <div className="p-8 bg-white relative" data-testid="plan-card-team">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5D4E] font-semibold mb-4">
              <Users className="w-3 h-3 inline mr-1" />
              {inTeam ? "Ton plan" : "Pour équipes"}
            </div>
            <div className="font-display font-black text-3xl tracking-tighter">
              Team
            </div>
            <div className="text-neutral-500 text-sm mt-1">Collabore en direct</div>

            <div className="mt-6 flex items-baseline gap-2">
              <div className="font-display font-black text-4xl tracking-tighter">
                29€
              </div>
              <div className="text-neutral-500 text-sm">/ mois</div>
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">
              5 sièges inclus · +6€/siège au-delà
            </div>

            <ul className="space-y-3 mt-8">
              {FEATURES_TEAM.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-[#4A5D4E] shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>

            <Button
              data-testid="checkout-team-btn"
              onClick={handleUpgradeTeam}
              disabled={loading !== null || inTeam}
              className="w-full mt-8 rounded-sm bg-[#4A5D4E] hover:bg-[#3B4B3F] text-white h-12 disabled:opacity-50"
            >
              {inTeam
                ? "Déjà dans une équipe"
                : loading === "team"
                ? "Redirection…"
                : "Passer à Team"}
            </Button>
            {isPro && !inTeam && (
              <p className="text-[10px] text-[#E05236] mt-3 text-center">
                Annule d'abord Pro pour éviter la double facturation.
              </p>
            )}
            <p className="text-[10px] text-neutral-500 mt-2 text-center">
              Paiement sécurisé Stripe · Sans engagement
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
